import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { 
  Search, ArrowRight, Shield, Clock, Star, 
  ChevronDown, Menu, X, Phone, Mail, MapPin, CheckCircle,
  Plus, Trash2, Package, Upload, FileSpreadsheet, Download
} from 'lucide-react';
import axios from 'axios';

interface Peca {
  id: string;
  tipo_equipamento: string;
  marca: string;
  model: string;
  tipo_peca: string;
  descricao: string;
  quantidade: number;
  urgencia: string;
}

interface FormData {
  tipo_equipamento: string;
  marca: string;
  model: string;
  tipo_peca: string;
  descricao: string;
  quantidade: number;
  urgencia: string;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pecas, setPecas] = useState<Peca[]>([]);
  const [showMultiPecas, setShowMultiPecas] = useState(false);
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [isProcessingExcel, setIsProcessingExcel] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm<FormData>();

  const addPeca = (data: FormData) => {
    const novaPeca: Peca = {
      id: Date.now().toString(),
      ...data
    };
    setPecas([...pecas, novaPeca]);
    reset();
    toast.success('Peça adicionada ao pedido!');
  };

  const removePeca = (id: string) => {
    setPecas(pecas.filter(peca => peca.id !== id));
    toast.success('Peça removida do pedido!');
  };

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
          file.type === 'application/vnd.ms-excel') {
        setExcelFile(file);
        toast.success('Arquivo Excel selecionado!');
      } else {
        toast.error('Por favor, selecione um arquivo Excel válido (.xlsx ou .xls)');
      }
    }
  };

  const processExcelFile = async () => {
    if (!excelFile) {
      toast.error('Selecione um arquivo Excel primeiro!');
      return;
    }

    setIsProcessingExcel(true);
    try {
      // Simular processamento do Excel
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Dados simulados do Excel (em produção, isso seria processado pelo backend)
      const pecasDoExcel: Peca[] = [
        {
          id: Date.now().toString() + '_1',
          tipo_equipamento: 'escavadeira',
          marca: 'caterpillar',
          model: '320D',
          tipo_peca: 'motor',
          descricao: 'Kit de reparo do motor completo',
          quantidade: 2,
          urgencia: 'alta'
        },
        {
          id: Date.now().toString() + '_2',
          tipo_equipamento: 'pá_carregadeira',
          marca: 'komatsu',
          model: 'WA380',
          tipo_peca: 'hidráulica',
          descricao: 'Bomba hidráulica principal',
          quantidade: 1,
          urgencia: 'urgente'
        },
        {
          id: Date.now().toString() + '_3',
          tipo_equipamento: 'trator',
          marca: 'john_deere',
          model: '750J',
          tipo_peca: 'transmissão',
          descricao: 'Conjunto de engrenagens da transmissão',
          quantidade: 1,
          urgencia: 'média'
        }
      ];

      setPecas(pecasDoExcel);
      setExcelFile(null);
      setShowExcelUpload(false);
      toast.success(`Excel processado com sucesso! ${pecasDoExcel.length} peças adicionadas.`);
    } catch (error) {
      toast.error('Erro ao processar arquivo Excel');
    } finally {
      setIsProcessingExcel(false);
    }
  };

  const downloadExcelTemplate = () => {
    // Criar um template Excel básico (em produção, seria um arquivo real)
    const templateData = [
      ['Tipo de Equipamento', 'Marca', 'Modelo', 'Tipo de Peça', 'Descrição', 'Quantidade', 'Urgência'],
      ['escavadeira', 'caterpillar', '320D', 'motor', 'Kit de reparo do motor', '1', 'alta'],
      ['pá_carregadeira', 'komatsu', 'WA380', 'hidráulica', 'Bomba hidráulica', '2', 'média'],
      ['', '', '', '', '', '', ''],
      ['', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '']
    ];

    let csvContent = "data:text/csv;charset=utf-8,";
    templateData.forEach(row => {
      csvContent += row.join(",") + "\r\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "template_orcamento_pecas.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Template baixado com sucesso!');
  };

  const onSubmitMultiPecas = async () => {
    if (pecas.length === 0) {
      toast.error('Adicione pelo menos uma peça ao pedido!');
      return;
    }

    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      localStorage.setItem('pedidoMultiPecas', JSON.stringify(pecas));
      toast.success(`Pedido com ${pecas.length} peças enviado com sucesso!`);
      navigate('/cadastro');
    } catch (error) {
      toast.error('Erro ao enviar pedido');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitSinglePeca = async (data: FormData) => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      localStorage.setItem('pedidoTemporario', JSON.stringify(data));
      toast.success('Pedido enviado com sucesso!');
      navigate('/cadastro');
    } catch (error) {
      toast.error('Erro ao enviar pedido');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img 
                src="/logo-pecaja.png" 
                alt="PeçaJá & Sobratema" 
                className="h-8 w-auto object-contain"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="/" className="nav-link">Home</a>
              <a href="#servicos" className="nav-link">Serviços</a>
              <a href="#sobre" className="nav-link">Sobre</a>
              <a href="#contato" className="nav-link">Contato</a>
              
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => navigate('/login')}
                  className="btn btn-outline"
                >
                  Entrar
                </button>
                <button 
                  onClick={() => navigate('/cadastro')}
                  className="btn btn-primary"
                >
                  Cadastrar
                </button>
              </div>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-4">
                <a href="/" className="nav-link">Home</a>
                <a href="#servicos" className="nav-link">Serviços</a>
                <a href="#sobre" className="nav-link">Sobre</a>
                <a href="#contato" className="nav-link">Contato</a>
                <div className="flex flex-col space-y-2 pt-4 border-t border-gray-200">
                  <button 
                    onClick={() => navigate('/login')}
                    className="btn btn-outline w-full"
                  >
                    Entrar
                  </button>
                  <button 
                    onClick={() => navigate('/cadastro')}
                    className="btn btn-primary w-full"
                  >
                    Cadastrar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Encontre as peças que você precisa
            </h1>
            <p className="text-xl text-white mb-12 max-w-2xl mx-auto">
              Conectamos você aos melhores fornecedores de peças para máquinas pesadas. 
              Receba orçamentos em minutos.
            </p>

            {/* Toggle between single and multi peças */}
            <div className="flex justify-center mb-8">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-1.5 flex shadow-lg border border-white/30">
                <button
                  onClick={() => setShowMultiPecas(false)}
                  className={`px-4 md:px-6 py-2.5 md:py-3 rounded-lg transition-all duration-300 flex items-center justify-center min-w-[120px] md:min-w-[140px] ${
                    !showMultiPecas 
                      ? 'bg-white text-red-600 font-semibold shadow-md transform scale-105' 
                      : 'text-white hover:bg-white/30 hover:scale-105'
                  }`}
                >
                  <Package className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" />
                  <span className="text-sm md:text-base font-medium">Peça Única</span>
                </button>
                <button
                  onClick={() => setShowMultiPecas(true)}
                  className={`px-4 md:px-6 py-2.5 md:py-3 rounded-lg transition-all duration-300 flex items-center justify-center min-w-[120px] md:min-w-[140px] ${
                    showMultiPecas 
                      ? 'bg-white text-red-600 font-semibold shadow-md transform scale-105' 
                      : 'text-white hover:bg-white/30 hover:scale-105'
                  }`}
                >
                  <Package className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" />
                  <span className="text-sm md:text-base font-medium">Multi Peças</span>
                </button>
              </div>
            </div>

            {/* Single Peça Form */}
            {!showMultiPecas && (
              <div className="max-w-2xl mx-auto">
                <form onSubmit={handleSubmit(onSubmitSinglePeca)} className="card shadow-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="form-label">Tipo de Equipamento</label>
                      <select {...register('tipo_equipamento', { required: true })} className="form-select">
                        <option value="">Selecione...</option>
                        <option value="escavadeira">Escavadeira</option>
                        <option value="pá_carregadeira">Pá Carregadeira</option>
                        <option value="retroescavadeira">Retroescavadeira</option>
                        <option value="trator">Trator</option>
                        <option value="empilhadeira">Empilhadeira</option>
                        <option value="caminhão">Caminhão</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Marca</label>
                      <select {...register('marca', { required: true })} className="form-select">
                        <option value="">Selecione...</option>
                        <option value="caterpillar">Caterpillar</option>
                        <option value="komatsu">Komatsu</option>
                        <option value="volvo">Volvo</option>
                        <option value="case">Case</option>
                        <option value="john_deere">John Deere</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="form-label">Modelo</label>
                      <input
                        type="text"
                        {...register('model', { required: true })}
                        className="form-input"
                        placeholder="Ex: 320D, WA380, etc."
                      />
                    </div>
                    <div>
                      <label className="form-label">Tipo de Peça</label>
                      <select {...register('tipo_peca', { required: true })} className="form-select">
                        <option value="">Selecione...</option>
                        <option value="motor">Motor</option>
                        <option value="transmissão">Transmissão</option>
                        <option value="hidráulica">Hidráulica</option>
                        <option value="elétrica">Elétrica</option>
                        <option value="suspensão">Suspensão</option>
                        <option value="freio">Freio</option>
                        <option value="filtro">Filtro</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="form-label">Descrição</label>
                    <textarea
                      {...register('descricao', { required: true })}
                      className="form-textarea"
                      rows={3}
                      placeholder="Descreva a peça que você precisa..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn btn-primary w-full"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="spinner mr-3"></div>
                        Enviando...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <Search className="w-5 h-5 mr-2" />
                        Encontrar Peças
                      </div>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Multi Peças Form */}
            {showMultiPecas && (
              <div className="max-w-4xl mx-auto">
                <div className="card shadow-lg">
                  {/* Opções de entrada */}
                  <div className="mb-6">
                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                      <button
                        onClick={() => setShowExcelUpload(false)}
                        className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all duration-300 flex items-center justify-center ${
                          !showExcelUpload
                            ? 'border-red-500 bg-red-50 text-red-700'
                            : 'border-gray-300 text-gray-600 hover:border-red-300'
                        }`}
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Adicionar Manualmente
                      </button>
                      <button
                        onClick={() => setShowExcelUpload(true)}
                        className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all duration-300 flex items-center justify-center ${
                          showExcelUpload
                            ? 'border-red-500 bg-red-50 text-red-700'
                            : 'border-gray-300 text-gray-600 hover:border-red-300'
                        }`}
                      >
                        <FileSpreadsheet className="w-5 h-5 mr-2" />
                        Upload Excel
                      </button>
                    </div>

                    {/* Botão para baixar template */}
                    <div className="text-center mb-4">
                      <button
                        onClick={downloadExcelTemplate}
                        className="inline-flex items-center px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Baixar Template Excel
                      </button>
                    </div>
                  </div>

                  {/* Form para adicionar peças manualmente */}
                  {!showExcelUpload && (
                    <form onSubmit={handleSubmit(addPeca)} className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Plus className="w-5 h-5 mr-2 text-red-600" />
                        Adicionar Nova Peça
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="form-label">Tipo de Equipamento</label>
                          <select {...register('tipo_equipamento', { required: true })} className="form-select">
                            <option value="">Selecione...</option>
                            <option value="escavadeira">Escavadeira</option>
                            <option value="pá_carregadeira">Pá Carregadeira</option>
                            <option value="retroescavadeira">Retroescavadeira</option>
                            <option value="trator">Trator</option>
                            <option value="empilhadeira">Empilhadeira</option>
                            <option value="caminhão">Caminhão</option>
                            <option value="outro">Outro</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Marca</label>
                          <select {...register('marca', { required: true })} className="form-select">
                            <option value="">Selecione...</option>
                            <option value="caterpillar">Caterpillar</option>
                            <option value="komatsu">Komatsu</option>
                            <option value="volvo">Volvo</option>
                            <option value="case">Case</option>
                            <option value="john_deere">John Deere</option>
                            <option value="outro">Outro</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Modelo</label>
                          <input
                            type="text"
                            {...register('model', { required: true })}
                            className="form-input"
                            placeholder="Ex: 320D, WA380"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <label className="form-label">Tipo de Peça</label>
                          <select {...register('tipo_peca', { required: true })} className="form-select">
                            <option value="">Selecione...</option>
                            <option value="motor">Motor</option>
                            <option value="transmissão">Transmissão</option>
                            <option value="hidráulica">Hidráulica</option>
                            <option value="elétrica">Elétrica</option>
                            <option value="suspensão">Suspensão</option>
                            <option value="freio">Freio</option>
                            <option value="filtro">Filtro</option>
                            <option value="outro">Outro</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Quantidade</label>
                          <input
                            type="number"
                            {...register('quantidade', { required: true, min: 1 })}
                            className="form-input"
                            min="1"
                            defaultValue="1"
                          />
                        </div>
                        <div>
                          <label className="form-label">Urgência</label>
                          <select {...register('urgencia', { required: true })} className="form-select">
                            <option value="">Selecione...</option>
                            <option value="baixa">Baixa</option>
                            <option value="média">Média</option>
                            <option value="alta">Alta</option>
                            <option value="urgente">Urgente</option>
                          </select>
                        </div>
                        <div className="flex items-end">
                          <button
                            type="submit"
                            className="btn btn-primary w-full"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Adicionar
                          </button>
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="form-label">Descrição</label>
                        <textarea
                          {...register('descricao', { required: true })}
                          className="form-textarea"
                          rows={2}
                          placeholder="Descreva a peça que você precisa..."
                        />
                      </div>
                    </form>
                  )}

                  {/* Upload de Excel */}
                  {showExcelUpload && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FileSpreadsheet className="w-5 h-5 mr-2 text-red-600" />
                        Upload de Arquivo Excel
                      </h3>
                      
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-400 transition-colors">
                        <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <div className="mb-4">
                          <label htmlFor="excel-upload" className="cursor-pointer">
                            <div className="text-lg font-medium text-gray-900 mb-2">
                              {excelFile ? excelFile.name : 'Clique para selecionar arquivo Excel'}
                            </div>
                            <div className="text-sm text-gray-500 mb-4">
                              Suporte para arquivos .xlsx e .xls
                            </div>
                            <input
                              id="excel-upload"
                              type="file"
                              accept=".xlsx,.xls"
                              onChange={handleExcelUpload}
                              className="hidden"
                            />
                            <button
                              type="button"
                              className="btn btn-outline"
                            >
                              Selecionar Arquivo
                            </button>
                          </label>
                        </div>
                        
                        {excelFile && (
                          <div className="space-y-3">
                            <div className="text-sm text-gray-600">
                              Arquivo selecionado: <span className="font-medium">{excelFile.name}</span>
                            </div>
                            <button
                              onClick={processExcelFile}
                              disabled={isProcessingExcel}
                              className="btn btn-primary"
                            >
                              {isProcessingExcel ? (
                                <div className="flex items-center">
                                  <div className="spinner mr-2"></div>
                                  Processando...
                                </div>
                              ) : (
                                <div className="flex items-center">
                                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                                  Processar Excel
                                </div>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Lista de peças adicionadas */}
                  {pecas.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Package className="w-5 h-5 mr-2 text-red-600" />
                        Peças no Pedido ({pecas.length})
                      </h3>
                      
                      <div className="space-y-3">
                        {pecas.map((peca, index) => (
                          <div key={peca.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                  <div>
                                    <span className="font-medium text-gray-700">Equipamento:</span>
                                    <span className="ml-2 text-gray-600 capitalize">{peca.tipo_equipamento.replace('_', ' ')}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">Marca/Modelo:</span>
                                    <span className="ml-2 text-gray-600">{peca.marca} {peca.model}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">Peça:</span>
                                    <span className="ml-2 text-gray-600 capitalize">{peca.tipo_peca}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">Qtd:</span>
                                    <span className="ml-2 text-gray-600">{peca.quantidade}</span>
                                  </div>
                                </div>
                                <div className="mt-2">
                                  <span className="font-medium text-gray-700">Descrição:</span>
                                  <span className="ml-2 text-gray-600">{peca.descricao}</span>
                                </div>
                                <div className="mt-1">
                                  <span className="font-medium text-gray-700">Urgência:</span>
                                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                                    peca.urgencia === 'urgente' ? 'bg-red-100 text-red-800' :
                                    peca.urgencia === 'alta' ? 'bg-orange-100 text-orange-800' :
                                    peca.urgencia === 'média' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    {peca.urgencia}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => removePeca(peca.id)}
                                className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remover peça"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Botão para enviar pedido multi peças */}
                  {pecas.length > 0 && (
                    <button
                      onClick={onSubmitMultiPecas}
                      disabled={isLoading}
                      className="btn btn-primary w-full text-lg py-4"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="spinner mr-3"></div>
                          Enviando Pedido com {pecas.length} Peças...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <Search className="w-6 h-6 mr-2" />
                          Enviar Pedido com {pecas.length} Peças
                        </div>
                      )}
                    </button>
                  )}

                  {/* Mensagem quando não há peças */}
                  {pecas.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Adicione peças ao seu pedido para começar</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="servicos" className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Como Funciona
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Processo simples e rápido para encontrar as peças que você precisa
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="feature-card">
              <div className="w-12 h-12 bg-red rounded-lg flex items-center justify-center mb-4 mx-auto">
                <Search className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                1. Descreva sua necessidade
              </h3>
              <p className="text-gray-600">
                Informe o tipo de equipamento, marca, modelo e peça que você precisa.
              </p>
            </div>

            <div className="feature-card">
              <div className="w-12 h-12 bg-red rounded-lg flex items-center justify-center mb-4 mx-auto">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                2. Receba orçamentos
              </h3>
              <p className="text-gray-600">
                Fornecedores qualificados entram em contato com propostas em minutos.
              </p>
            </div>

            <div className="feature-card">
              <div className="w-12 h-12 bg-red rounded-lg flex items-center justify-center mb-4 mx-auto">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                3. Escolha o melhor
              </h3>
              <p className="text-gray-600">
                Compare preços, prazos e qualidade para fazer a melhor escolha.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="stats-card">
              <div className="text-center">
                <div className="text-3xl font-bold text-red mb-2">500+</div>
                <p className="text-gray-600">Fornecedores Cadastrados</p>
              </div>
            </div>
            <div className="stats-card">
              <div className="text-center">
                <div className="text-3xl font-bold text-red mb-2">10.000+</div>
                <p className="text-gray-600">Pedidos Atendidos</p>
              </div>
            </div>
            <div className="stats-card">
              <div className="text-center">
                <div className="text-3xl font-bold text-red mb-2">98%</div>
                <p className="text-gray-600">Satisfação dos Clientes</p>
              </div>
            </div>
            <div className="stats-card">
              <div className="text-center">
                <div className="text-3xl font-bold text-red mb-2">24h</div>
                <p className="text-gray-600">Tempo Médio de Resposta</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Pronto para começar?
            </h2>
            <p className="text-xl text-white mb-8 opacity-95">
              Junte-se a milhares de empresas que já encontraram as peças que precisavam.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => navigate('/cadastro')}
                className="btn bg-red text-white hover:bg-red/90 font-semibold px-12 py-4 rounded-lg shadow-lg transition-all duration-200 hover:shadow-xl transform hover:scale-105"
              >
                Cadastrar-se Agora
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="/logo-pecaja.png" 
                  alt="PeçaJá & Sobratema" 
                  className="h-8 w-auto object-contain filter brightness-0 invert"
                />
              </div>
              <p className="text-gray-300">
                Conectando quem precisa com quem tem. A plataforma mais completa para peças de máquinas pesadas.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Empresa</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-white transition-colors">Sobre Nós</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Carreiras</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Imprensa</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Suporte</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-white transition-colors">Central de Ajuda</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contato</a></li>
                <li><a href="#" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-white transition-colors">Termos de Uso</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacidade</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Cookies</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Licenças</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-300 text-sm">
                © 2024 PeçaJá. Todos os direitos reservados.
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <a href="#" className="text-gray-300 hover:text-white transition-colors">
                  <Phone className="w-5 h-5" />
                </a>
                <a href="#" className="text-gray-300 hover:text-white transition-colors">
                  <Mail className="w-5 h-5" />
                </a>
                <a href="#" className="text-gray-300 hover:text-white transition-colors">
                  <MapPin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage; 